/**
 * Fragment de routes du paquet v4-p02-documents-ecrans-2 (vague V4 ; règle R2 du découpage V4–V11 : un fragment par
 * paquet, `routes/v4.tsx` interdit ; disjoint du fragment `v4-documents-1`), rendu par le registre v10. Composants chargés
 * à la demande, aucun code exécuté au chargement. Routes du socle remplacées (`remplace: true`), d'après le
 * `couvre: route:` des YAML d'écrans :
 *  - `/specifications` et `/specifications/modifications` → projet-redaction (onglets documents et révisions ; les écrans
 *    historiques `SpecificationsScreen` et `DemandesModificationListe` ne sont plus servis à ces adresses) ;
 *  - `/specifications/modifications/:id` → projet-revision-fiche (remplace `DemandeModificationFiche`) ;
 *  - `/artifacts` → projet-documents-publies (remplace `ArtifactsBrowser`) ;
 *  - `/roadmap` → projet-feuille-de-route (remplace la redirection vers `/projet?vue=roadmap`).
 * Routes neuves (objet par l'adresse, `?projet=` transmis) :
 *  - `/roadmap/reperes/:id` → projet-repere-fiche ;
 *  - `/projet/portes` → projet-portes ; `/projet/portes/:id` → projet-porte-fiche ;
 *  - `/projet/rediger-document` → dialogue-rediger-premier-document ;
 *  - `/projet/solliciter-derogation` → dialogue-solliciter-derogation.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetRedaction = lazy(() => import('../ecrans/v4/documents-2/ProjetRedaction'))
const ProjetRevisionFiche = lazy(() => import('../ecrans/v4/documents-2/ProjetRevisionFiche'))
const ProjetDocumentsPublies = lazy(() => import('../ecrans/v4/documents-2/ProjetDocumentsPublies'))
const ProjetFeuilleDeRoute = lazy(() => import('../ecrans/v4/documents-2/ProjetFeuilleDeRoute'))
const ProjetRepereFiche = lazy(() => import('../ecrans/v4/documents-2/ProjetRepereFiche'))
const ProjetPortes = lazy(() => import('../ecrans/v4/documents-2/ProjetPortes'))
const ProjetPorteFiche = lazy(() => import('../ecrans/v4/documents-2/ProjetPorteFiche'))
const DialogueRedigerPremierDocument = lazy(() => import('../ecrans/v4/documents-2/DialogueRedigerPremierDocument'))
const DialogueSolliciterDerogation = lazy(() => import('../ecrans/v4/documents-2/DialogueSolliciterDerogation'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v4-documents-2',
  routes: [
    { chemin: '/specifications', element: ecran(ProjetRedaction), remplace: true },
    { chemin: '/specifications/modifications', element: ecran(ProjetRedaction), remplace: true },
    { chemin: '/specifications/modifications/:id', element: ecran(ProjetRevisionFiche), remplace: true },
    { chemin: '/artifacts', element: ecran(ProjetDocumentsPublies), remplace: true },
    { chemin: '/roadmap', element: ecran(ProjetFeuilleDeRoute), remplace: true },
    { chemin: '/roadmap/reperes/:id', element: ecran(ProjetRepereFiche) },
    { chemin: '/projet/portes', element: ecran(ProjetPortes) },
    { chemin: '/projet/portes/:id', element: ecran(ProjetPorteFiche) },
    { chemin: '/projet/rediger-document', element: ecran(DialogueRedigerPremierDocument) },
    { chemin: '/projet/solliciter-derogation', element: ecran(DialogueSolliciterDerogation) },
  ],
}
